import { Injectable } from '@angular/core';
import { RuleType } from '@twaice-fe/shared/models';
import { distinctUntilChanged, filter, map, Observable, withLatestFrom } from 'rxjs';
import { ComponentSidebarSorterService } from './component-sidebar-sorter.service';
import {
  AlertSeverity,
  AlertSortBy,
  BreachedRule,
  BreachedRuleSummary,
  BreachSummaryRule,
  ThresholdRuleVersion,
  ThresholdSeverity,
} from './component-sidebar.types';

const thresholdSeverityToAlertSeverityMap = {
  [ThresholdSeverity.High]: AlertSeverity.High,
  [ThresholdSeverity.Medium]: AlertSeverity.Medium,
  [ThresholdSeverity.Minor]: AlertSeverity.Minor,
};

export interface BreachedRulesParams {
  breaches$: Observable<BreachedRule[]>;
  rules$: Observable<Record<string, BreachSummaryRule>>;
  sidebarState$: Observable<{ sortBy: AlertSortBy; componentBk: string | null; ruleBk: string | undefined }>;
}

@Injectable()
export abstract class ComponentSidebarService {
  protected constructor(private componentSidebarSorterService: ComponentSidebarSorterService) {}

  protected filterBreachedRulesByComponentBk(rules: BreachedRule[], componentBk: string) {
    const breachedRules = [];

    for (const breachedRule of rules) {
      if (breachedRule.componentBk === componentBk) {
        breachedRules.push(breachedRule);
      }
    }

    return breachedRules;
  }

  protected getBreachedRulesWithSorting$({
    breaches$,
    rules$,
    sidebarState$,
  }: BreachedRulesParams): Observable<BreachedRuleSummary[]> {
    return sidebarState$.pipe(
      filter(
        (sidebarState): sidebarState is { sortBy: AlertSortBy; componentBk: string; ruleBk: string | undefined } =>
          !!sidebarState.componentBk
      ),
      distinctUntilChanged((lhs, rhs) => JSON.stringify(lhs) === JSON.stringify(rhs)),
      withLatestFrom(rules$, breaches$),
      map(([{ componentBk, sortBy, ruleBk }, rules, breaches]) => {
        const filteredBreaches = this.filterBreachedRulesByComponentBk(breaches, componentBk);
        return {
          breaches: filteredBreaches,
          rules,
          sidebarState: { componentBk, sortBy, ruleBk },
        };
      }),
      map(({ breaches, rules, sidebarState: { sortBy, ruleBk } }) =>
        this.componentSidebarSorterService.sortBreaches(sortBy, this.getBreachedRuleSummary({ rules, breaches }), ruleBk)
      )
    );
  }

  protected getBreachedRuleSummary({
    rules,
    breaches,
  }: {
    rules: Record<string, BreachSummaryRule>;
    breaches: BreachedRule[];
  }): BreachedRuleSummary[] {
    return breaches.map((breach) => {
      const currentRule = rules[breach.ruleBk];
      const breachedVersion = currentRule.versions.find(
        (version) => version.versionNumber === breach.highestSeverityBreachedRuleVersion
      ) as ThresholdRuleVersion;

      if (!breachedVersion) {
        throw new Error('No breached rule version found');
      }

      if (currentRule.type === RuleType.BMSRule) {
        const sortedVersions = [...currentRule.versions].sort((a, b) => a.versionNumber - b.versionNumber);
        const currentVersion = sortedVersions[0];
        return {
          comparisonOperator: null,
          highestBreachedThreshold: null,
          highestSeverity: thresholdSeverityToAlertSeverityMap[breach.highestSeverity],
          isBreachOnOldVersion: currentVersion.versionNumber > breachedVersion.versionNumber,
          isOngoing: breach.isOngoing,
          lastBreach: breach.lastBreach as string,
          ruleBk: currentRule.ruleBk,
          ruleName: currentRule.ruleName,
          type: currentRule.type,
          unit: null,
        };
      }

      return {
        comparisonOperator: breachedVersion.comparisonOperator,
        highestBreachedThreshold: this.mapFromVersionThreshold(breachedVersion, breach.highestSeverity) as number,
        highestSeverity: thresholdSeverityToAlertSeverityMap[breach.highestSeverity],
        isBreachOnOldVersion: currentRule.currentVersion.versionNumber > breachedVersion.versionNumber,
        isOngoing: breach.isOngoing,
        lastBreach: breach.lastBreach as string,
        ruleBk: currentRule.ruleBk,
        ruleName: currentRule.ruleName,
        type: currentRule.type,
        unit: currentRule.unit,
      };
    });
  }

  private mapFromVersionThreshold(version: ThresholdRuleVersion, severity: ThresholdSeverity) {
    return {
      [ThresholdSeverity.High]: version.highThreshold,
      [ThresholdSeverity.Medium]: version.mediumThreshold,
      [ThresholdSeverity.Minor]: version.minorThreshold,
    }[severity];
  }

  abstract getBreachedRules$(): Observable<BreachedRuleSummary[]>;
  abstract getSelectedRule$(): Observable<string>;
  abstract getSelectedComponent$(): Observable<string>;
  abstract getSelectedDate$(): Observable<Date | null>;
  abstract getStorageTimezone$(): Observable<string>;
  abstract hasNavigteToDataExplorerAction$(ruleBk?: string): Observable<boolean>;
  abstract fetchRuleEvaluations(ruleBk: string): void;
  abstract navigateToDataExplorer(ruleBk: string): void;
}
